import React, { useState } from 'react';
import StrongsModal from './StrongsModal';

/**
 * Renders a single Strong's-tagged verse as a word-aligned interlinear strip.
 *
 * Props:
 *  - text:         raw verse text (may contain `<w s='G2316'>God</w>` markers)
 *  - lexicon:      `{ "G2316": { lemma, translit, pronunciation, ... } }`
 *  - fontSizeClass / fontFamilyClass: styling for the English row
 *  - translation:  current translation code (passed into the modal)
 *
 * Tagged words render as a 3-row vertical stack:
 *    English  →  Original (Hebrew/Greek)  →  translit · Strong's #
 * Plain words render as a single English row aligned to the same baseline.
 */
const STRONGS_RE = /<w\s+s=['"]([HG]\d+)['"]>([^<]+)<\/w>/g;

const parseTokens = (text) => {
  if (!text) return [];
  const tokens = [];
  let lastIndex = 0;
  let match;
  STRONGS_RE.lastIndex = 0;
  while ((match = STRONGS_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plain = text.substring(lastIndex, match.index);
      // Split on whitespace so layout flows naturally
      plain.split(/(\s+)/).forEach((chunk) => {
        if (chunk.trim()) tokens.push({ type: 'plain', word: chunk });
        else if (chunk) tokens.push({ type: 'space' });
      });
    }
    tokens.push({ type: 'strongs', strongs: match[1], word: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    const tail = text.substring(lastIndex);
    tail.split(/(\s+)/).forEach((chunk) => {
      if (chunk.trim()) tokens.push({ type: 'plain', word: chunk });
      else if (chunk) tokens.push({ type: 'space' });
    });
  }
  return tokens;
};

const InterlinearVerse = ({
  text,
  lexicon = {},
  fontSizeClass = 'text-base',
  fontFamilyClass = 'font-serif',
  translation = 'KJV',
}) => {
  const [activeStrongs, setActiveStrongs] = useState(null);
  const tokens = parseTokens(text);

  return (
    <>
      <div
        className={`flex flex-wrap items-end gap-x-3 gap-y-2 ${fontSizeClass} ${fontFamilyClass}`}
        data-testid="interlinear-verse"
      >
        {tokens.map((tok, i) => {
          if (tok.type === 'space') {
            return <span key={i} className="w-1" />;
          }
          if (tok.type === 'plain') {
            return (
              <span
                key={i}
                className="inline-flex flex-col items-start text-foreground/90 leading-tight"
              >
                <span>{tok.word}</span>
              </span>
            );
          }
          const lex = lexicon[tok.strongs] || {};
          return (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveStrongs(tok.strongs);
              }}
              data-testid={`interlinear-word-${tok.strongs}`}
              className="inline-flex flex-col items-start leading-tight rounded px-1 py-0.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors text-left cursor-pointer border-b border-blue-300/60 dark:border-blue-600/60"
              title={`Strong's ${tok.strongs}`}
            >
              <span className="text-blue-700 dark:text-blue-300 font-medium">
                {tok.word}
              </span>
              {lex.lemma && (
                <span className="text-[13px] font-serif text-purple-700 dark:text-purple-300 mt-0.5">
                  {lex.lemma}
                </span>
              )}
              <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 mt-0.5">
                {lex.translit ? `${lex.translit} · ` : ''}
                {tok.strongs}
              </span>
            </button>
          );
        })}
      </div>

      <StrongsModal
        strongsId={activeStrongs}
        isOpen={!!activeStrongs}
        onClose={() => setActiveStrongs(null)}
        translation={translation}
      />
    </>
  );
};

export default InterlinearVerse;
